import cn from 'classnames';
import React, { FC, memo, useState } from 'react';
import { customDataLayerPush } from 'src/utils/analytics';
import { DL_EVENT, PAGE_SLOT } from 'src/utils/analytics/constants';
import SubMenu from '../SubMenu';
import { nav } from '_utils/navigation';

interface ColumnItemProps {
  columnWidth: number;
  highlight: boolean | null;
  backgroundColor: boolean | null;
  hideOnMobile: boolean | null;
  items: Item[];
  contentful_id: string;
  title: string;
  target?: string;
}
interface NavItemProps {
  id: string;
  text?: string;
  navDropdownId?: string;
  gridColumns?: number;
  navDropdownTitle?: string;
  link?: string;
  gridColumnItems?: Array<ColumnItemProps>;
  node_locale?: string;
  menuCta?: {
    title: string;
    buttonLink: string;
    buttonText: string;
    buttonId?: string;
    ctaDescription: {
      raw: string;
    };
  };
}
interface MegNavProps {
  navItems?: Array<NavItemProps>;
  showMobileNav?: boolean;
  visibleNav: boolean;
}

const MegNav: FC<MegNavProps> = props => {
  const { navItems, showMobileNav, visibleNav } = props;
  const [activeSubMenu, setActiveSubMenu] = useState<number | null>(null);

  const handleMouseLeave = e => {
    const relatedTarget = e?.relatedTarget;
    const currentTarget = e?.currentTarget as Node;

    if (
      relatedTarget &&
      currentTarget instanceof Node &&
      relatedTarget.parentNode !== currentTarget &&
      relatedTarget !== window &&
      relatedTarget.id !== 'sub-menu' &&
      !relatedTarget.closest('#sub-menu')
    ) {
      setActiveSubMenu(null);
    }
  };

  return (
    <>
      <nav
        className={cn(
          'h-[calc(100%-130px)] lg:h-auto p-8 lg:p-0',
          showMobileNav && 'overflow-y-scroll'
        )}
      >
        <ul className="flex flex-col lg:flex-row text-lg lg:gap-2 font-semibold">
          {navItems?.map((item, i) => {
            if (item.navDropdownTitle !== null) {
              return (
                <li
                  className="z-20 cursor-pointer px-8 border-animation group flex flex-col lg:flex-row items-center hover:text-indigo-1 py-4 lg:hover:[&>span]:after:border-b-2 lg:hover:[&>span]:after:border-indigo-1 "
                  key={item.id}
                  onClick={() => {
                    item?.link
                      ? (customDataLayerPush(DL_EVENT.elementClick, {pageSlot: PAGE_SLOT.Navi, label: item.text }),
                        nav(item.link))
                      : setActiveSubMenu(activeSubMenu === i ? null : i);
                  }}
                  onMouseEnter={() => setActiveSubMenu(i)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="w-full relative border-b-2 border-gray-5-light pb-1 lg:border-none lg:pb-0">
                    {item.navDropdownTitle}
                    {item.navDropdownTitle && item.gridColumnItems && (
                      <span
                        className={cn(
                          'inline-flex group-hover:text-indigo-1 ml-2 float-right lg:float-none lg:rotate-90  lg:group-hover:rotate-0 justify-center transition-all items-center w-4 h-4',
                          activeSubMenu === i && 'rotate-90'
                        )}
                      >
                        <svg
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                          className="block fill-current"
                          width="12px"
                          height="12px"
                          fillRule="evenodd"
                        >
                          <path
                            clipRule="evenodd"
                            d="M9.9 8 4.217 3.079a.646.646 0 0 1-.078-.886l.775-.967a.59.59 0 0 1 .852-.08l6.798 5.888c.276.239.435.593.435.966s-.16.727-.435.966l-6.798 5.888a.59.59 0 0 1-.852-.08l-.775-.967a.646.646 0 0 1 .078-.886L9.899 8Z"
                          ></path>
                        </svg>
                      </span>
                    )}
                  </div>

                  {item.gridColumnItems && activeSubMenu === i && visibleNav && (
                    <SubMenu
                      setActiveSubMenu={setActiveSubMenu}
                      gridColumnItems={item.gridColumnItems}
                      gridColumns={item.gridColumns}
                      menuCta={item.menuCta}
                      navDropdownId={item?.navDropdownId}
                      locale={item?.node_locale}
                    />
                  )}
                </li>
              );
            }
          })}
        </ul>
      </nav>
    </>
  );
};

export default memo(MegNav);
